import BoltIcon from '@mui/icons-material/Bolt';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { fetchTenantOperationalCampaigns } from '../services/tenantMetrics';
import type {
  TenantOperationalCampaignRow,
  TenantOperationalCampaignsResponse,
  TenantOperationalRuntimeCampaignLoadRow,
} from '../types';
import {
  CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY,
  CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS,
  buildCampaignSuspendedReasonI18nOptions,
} from '../utils/campaignOperationalStatus';
import LinkTableCell from './LinkTableCell';

interface TenantConcurrencyPressureIndicatorProps {
  occupiedConcurrency: number;
  totalConcurrency: number;
  campaignLoadRows?: TenantOperationalRuntimeCampaignLoadRow[];
}

function clampConcurrency(value: number | undefined | null): number {
  return Math.max(Number(value || 0), 0);
}

function resolveIndicatorMinWidth(totalConcurrency: number): number {
  const digits = String(Math.max(totalConcurrency, 0)).length;
  if (digits <= 1) {
    return 80;
  }
  if (digits === 2) {
    return 92;
  }
  if (digits === 3) {
    return 120;
  }
  return 170;
}

/**
 * Render a compact tenant-wide concurrency pressure indicator for the shell header.
 *
 * The pill mirrors the minutes indicator footprint, but uses one full-bleed
 * occupancy track to show how much contracted concurrency is currently busy.
 */
export default function TenantConcurrencyPressureIndicator({
  occupiedConcurrency,
  totalConcurrency,
  campaignLoadRows = [],
}: TenantConcurrencyPressureIndicatorProps) {
  const { t } = useTranslation(['common', 'pages']);
  const { currentTenant, user } = useAuth();
  const { formatNumber } = useNumberFormat();
  const tenantKey = currentTenant?.id || user?.tenantId || null;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [campaignsPayload, setCampaignsPayload] = useState<TenantOperationalCampaignsResponse | null>(null);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [campaignsError, setCampaignsError] = useState(false);

  const normalizedOccupiedConcurrency = clampConcurrency(occupiedConcurrency);
  const normalizedTotalConcurrency = clampConcurrency(totalConcurrency);
  const normalizedCampaignLoadRows = useMemo(
    () => campaignLoadRows.filter((row) => row.activePressure > 0),
    [campaignLoadRows],
  );

  const fillPercent = useMemo(() => {
    if (normalizedTotalConcurrency <= 0) {
      return 0;
    }
    return Math.max(
      0,
      Math.min((normalizedOccupiedConcurrency / normalizedTotalConcurrency) * 100, 100),
    );
  }, [normalizedOccupiedConcurrency, normalizedTotalConcurrency]);
  const minWidth = useMemo(
    () => resolveIndicatorMinWidth(normalizedTotalConcurrency),
    [normalizedTotalConcurrency],
  );
  const hasActiveLoad = normalizedOccupiedConcurrency > 0 && normalizedCampaignLoadRows.length > 0;
  const runningCampaignsWithoutCurrentLoad = useMemo(() => {
    const activeLoadCampaignIds = new Set(
      normalizedCampaignLoadRows
        .map((row) => row.campaignId)
        .filter((campaignId): campaignId is string => Boolean(campaignId)),
    );
    return (campaignsPayload?.runningCampaigns ?? []).filter(
      (campaign) => !activeLoadCampaignIds.has(campaign.id),
    );
  }, [campaignsPayload?.runningCampaigns, normalizedCampaignLoadRows]);
  const suspendedCampaigns = campaignsPayload?.suspendedCampaigns ?? [];

  useEffect(() => {
    setCampaignsPayload(null);
    setCampaignsError(false);
  }, [tenantKey]);

  useEffect(() => {
    if (!detailsOpen || campaignsPayload) {
      return;
    }

    let isMounted = true;
    setIsLoadingCampaigns(true);
    setCampaignsError(false);
    fetchTenantOperationalCampaigns()
      .then((payload) => {
        if (isMounted) {
          setCampaignsPayload(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCampaignsError(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingCampaigns(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [campaignsPayload, detailsOpen]);

  if (normalizedTotalConcurrency <= 0) {
    return null;
  }

  const handleOpenDetails = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDetailsOpen(true);
  };

  const handleOpenDetailsKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    setDetailsOpen(true);
  };

  const indicator = (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        alignItems: 'center',
        gap: '3px',
        opacity: normalizedOccupiedConcurrency > 0 ? 1 : 0.4,
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
      }}
      onClick={handleOpenDetails}
      onKeyDown={handleOpenDetailsKeyDown}
      role="button"
      tabIndex={0}
    >
      <BoltIcon
        data-testid="tenant-concurrency-pressure-icon"
        sx={{
          color: 'text.secondary',
          fontSize: 23,
          flexShrink: 0,
        }}
      />
      <Box
        data-testid="tenant-concurrency-pressure-root"
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth,
          maxWidth: 216,
          height: 40,
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.28)',
        }}
      >
        <Box
          data-testid="tenant-concurrency-pressure-track"
          style={{ width: `${fillPercent}%` }}
          sx={{
            position: 'absolute',
            inset: 0,
            right: 'auto',
            bgcolor: 'rgb(23 159 104)',
            transition: 'width 0.25s ease',
          }}
        />
        <Typography
          data-testid="tenant-concurrency-pressure-label"
          sx={{
            position: 'relative',
            zIndex: 1,
            color: '#FFFFFF',
            fontFamily: '"Barlow Condensed", "Oswald", sans-serif',
            fontSize: '1.3rem',
            fontWeight: 700,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            userSelect: 'none',
          }}
        >
          {formatNumber(normalizedOccupiedConcurrency)}
          /
          {formatNumber(normalizedTotalConcurrency)}
        </Typography>
      </Box>
    </Box>
  );

  const tooltipTitle = hasActiveLoad
    ? (
      normalizedCampaignLoadRows.length === 1
        ? t('common:subscription.headerConcurrency.runningSingle')
        : t('common:subscription.headerConcurrency.runningMultiple', {
          count: normalizedCampaignLoadRows.length,
        })
    )
    : t('common:subscription.headerConcurrency.empty');

  const renderCampaignTypeLabel = (campaignType?: TenantOperationalRuntimeCampaignLoadRow['campaignType']) => {
    if (!campaignType) {
      return '—';
    }

    return t(`pages:campaigns.typeChipLabels.${campaignType}`);
  };
  const renderSuspendedReasonLabel = (campaign: TenantOperationalCampaignRow) => {
    if (!campaign.operationalStatusReason) {
      return t('common:subscription.headerConcurrency.reasonUnknown');
    }
    const key = CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS[
      campaign.operationalStatusReason as keyof typeof CAMPAIGN_SUSPENDED_REASON_LABEL_KEYS
    ];
    if (!key) {
      return campaign.operationalStatusReason;
    }
    return t(
      key,
      buildCampaignSuspendedReasonI18nOptions(campaign, {
        nextRun: '—',
        transferTargetFallback: t(CAMPAIGN_TRANSFER_TARGET_FALLBACK_KEY),
      }),
    );
  };

  const renderLoadRows = () => (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('common:subscription.headerConcurrency.columns.campaignType')}</TableCell>
            <TableCell>{t('common:subscription.headerConcurrency.columns.campaignName')}</TableCell>
            <TableCell align="right">{t('common:subscription.headerConcurrency.columns.concurrency')}</TableCell>
            <TableCell align="right">{t('common:subscription.headerConcurrency.columns.currentLoad')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {normalizedCampaignLoadRows.map((row) => {
            const key = `${row.campaignId || row.campaignName || 'tenant-runtime-campaign'}-${row.activePressure}`;
            const campaignName = row.campaignName || t('pages:dashboard.noCampaign');
            const concurrencyValue = row.maxConcurrentCalls != null
              ? formatNumber(row.maxConcurrentCalls)
              : '—';
            const currentLoadValue = formatNumber(row.activePressure);

            if (!row.campaignId) {
              return (
                <TableRow key={key} hover>
                  <TableCell>{renderCampaignTypeLabel(row.campaignType)}</TableCell>
                  <TableCell>{campaignName}</TableCell>
                  <TableCell align="right">{concurrencyValue}</TableCell>
                  <TableCell align="right">{currentLoadValue}</TableCell>
                </TableRow>
              );
            }

            const detailLink = `/campaigns/${row.campaignId}`;
            return (
              <TableRow key={key} hover>
                <LinkTableCell to={detailLink}>
                  {renderCampaignTypeLabel(row.campaignType)}
                </LinkTableCell>
                <LinkTableCell to={detailLink}>
                  {campaignName}
                </LinkTableCell>
                <LinkTableCell to={detailLink} align="right">
                  {concurrencyValue}
                </LinkTableCell>
                <LinkTableCell to={detailLink} align="right">
                  {currentLoadValue}
                </LinkTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderOperationalCampaignRows = (
    rows: TenantOperationalCampaignRow[],
    options: { includeReason?: boolean } = {},
  ) => (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('common:subscription.headerConcurrency.columns.campaignType')}</TableCell>
            <TableCell>{t('common:subscription.headerConcurrency.columns.campaignName')}</TableCell>
            <TableCell align="right">{t('common:subscription.headerConcurrency.columns.concurrency')}</TableCell>
            {options.includeReason ? (
              <TableCell>{t('common:subscription.headerConcurrency.columns.reason')}</TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((campaign) => (
            <TableRow key={campaign.id} hover>
              <LinkTableCell to={campaign.path}>
                {renderCampaignTypeLabel(campaign.campaignType)}
              </LinkTableCell>
              <LinkTableCell to={campaign.path}>
                {campaign.name}
              </LinkTableCell>
              <LinkTableCell to={campaign.path} align="right">
                {campaign.maxConcurrentCalls != null ? formatNumber(campaign.maxConcurrentCalls) : '—'}
              </LinkTableCell>
              {options.includeReason ? (
                <LinkTableCell to={campaign.path}>
                  {renderSuspendedReasonLabel(campaign)}
                </LinkTableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Tooltip
        title={(
          <Box sx={{ py: 0.35 }}>
            <Typography sx={{ fontSize: '0.7rem', mb: hasActiveLoad ? 0.35 : 0 }}>
              {tooltipTitle}
            </Typography>
            <Button
              type="button"
              color="inherit"
              onClick={handleOpenDetails}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: 0,
                p: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '0.68rem',
                fontWeight: 600,
                lineHeight: 1.2,
                textDecoration: 'underline',
                textTransform: 'none',
              }}
            >
              {t('common:subscription.headerConcurrency.viewDetails')}
            </Button>
          </Box>
        )}
        arrow
        placement="bottom-end"
      >
        {indicator}
      </Tooltip>
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {t('common:subscription.headerConcurrency.detailsTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2}>
            <Box>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                {t('common:subscription.headerConcurrency.sections.currentLoad')}
              </Typography>
              {normalizedCampaignLoadRows.length ? renderLoadRows() : (
                <Typography color="text.secondary">
                  {t('common:subscription.headerConcurrency.empty')}
                </Typography>
              )}
            </Box>

            <Divider />

            {isLoadingCampaigns ? (
              <Stack direction="row" gap={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography color="text.secondary">
                  {t('common:subscription.headerConcurrency.loadingCampaigns')}
                </Typography>
              </Stack>
            ) : null}

            {campaignsError ? (
              <Alert severity="error">
                {t('common:subscription.headerConcurrency.loadCampaignsFailed')}
              </Alert>
            ) : null}

            {!isLoadingCampaigns && !campaignsError ? (
              <>
                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    {t('common:subscription.headerConcurrency.sections.runningWithoutLoad')}
                  </Typography>
                  {runningCampaignsWithoutCurrentLoad.length ? (
                    renderOperationalCampaignRows(runningCampaignsWithoutCurrentLoad)
                  ) : (
                    <Typography color="text.secondary">
                      {t('common:subscription.headerConcurrency.emptyRunningWithoutLoad')}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 700, mb: 1 }}>
                    {t('common:subscription.headerConcurrency.sections.suspended')}
                  </Typography>
                  {suspendedCampaigns.length ? (
                    renderOperationalCampaignRows(suspendedCampaigns, { includeReason: true })
                  ) : (
                    <Typography color="text.secondary">
                      {t('common:subscription.headerConcurrency.emptySuspended')}
                    </Typography>
                  )}
                </Box>
              </>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
